// App entry — AuthProvider + MUI ThemeProvider + Router + ErrorBoundary.
//
// Routes:
//   /login                                          -> publieke login
//   /                                               -> projecten-lijst (auth)
//   /projects                                       -> alias → /
//   /projects/new                                   -> intake nieuw project
//   /projects/:projectId/wizard                     -> wizard-shell (auto-redirect naar eerste cluster)
//   /projects/:projectId/wizard/:clusterId          -> één cluster
//   /projects/:projectId/risks                      -> risicoregister
//   /projects/:projectId/report                     -> rapport + PDF
//   /accounts                                       -> accountbeheer (beheerders)

import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AuthProvider, useAuth } from '@rvdh/react-auth-context';
import ErrorBoundary from '@rvdh/error-boundary';
import {
    requestHandler,
    setUnauthorizedHandler,
} from '@rvdh/shared-http-fetch';

import './i18n/i18n.js';
import { startVersionCheck } from './versionCheck.js';
import theme from './theme.js';
import { endpoints as apiEndpoints } from './api/client.js';

import LoginPage from './pages/LoginPage.jsx';
import ProjectsListPage from './pages/ProjectsListPage.jsx';
import NewProjectPage from './pages/NewProjectPage.jsx';
import WizardPage, { WizardIndex } from './pages/WizardPage.jsx';
import ClusterRunnerPage from './pages/ClusterRunnerPage.jsx';
import RiskRegisterPage from './pages/RiskRegisterPage.jsx';
import ReportPage from './pages/ReportPage.jsx';
import ChangePasswordPage from './pages/ChangePasswordPage.jsx';
import AccountsPage from './pages/AccountsPage.jsx';

// 401 op een willekeurig verzoek: sessie verlopen. Terug naar de login;
// niet-verzonden invoer staat in localStorage en gaat na inloggen alsnog mee.
setUnauthorizedHandler(() => {
    try { window.localStorage.removeItem('zbs:lastSession'); } catch { /* geen opslag */ }
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
});

const SESSION_KEY = 'zbs:lastSession';

function rememberSession(session) {
    try {
        if (session?.username) {
            window.localStorage.setItem(SESSION_KEY, JSON.stringify({
                username: session.username,
                role: session.role,
                password_change_required: Boolean(session.password_change_required),
            }));
        } else {
            window.localStorage.removeItem(SESSION_KEY);
        }
    } catch { /* opslag niet beschikbaar */ }
}

/**
 * Fetcher voor AuthProvider.
 *  - Sessiecontrole: AuthProvider vraagt POST op het verify-endpoint, de
 *    backend biedt GET /api/me. Zonder verbinding blijft de laatst bekende
 *    sessie gelden, zodat de app in het veld bruikbaar blijft; een echte 401
 *    logt altijd uit.
 *  - Rolconfiguratie bestaat hier niet: geen verzoek, geen override.
 */
async function authFetcher(url, method, body) {
    if (url === apiEndpoints.me) {
        if (method === 'GET') { return null; }
        try {
            const session = await requestHandler(url, 'GET');
            rememberSession(session);
            return session;
        } catch (err) {
            if (err?.statusCode) {
                if (err.statusCode === 401 || err.statusCode === 403) { rememberSession(null); }
                throw err;
            }
            const cached = JSON.parse(window.localStorage.getItem(SESSION_KEY) || 'null');
            if (cached?.username) { return cached; }
            throw err;
        }
    }
    const data = await requestHandler(url, method, body);
    if (url === apiEndpoints.login) { rememberSession(data); }
    return data;
}

/** Route-guard voor pagina's die auth vereisen. */
function RequireAuth({ children, allowPasswordChange = false }) {
    const { isAuthenticated, authChecked, passwordChangeRequired } = useAuth();
    const location = useLocation();

    if (!authChecked) {
        return null; // AuthProvider is bezig met verify
    }
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // Tijdelijk wachtwoord: eerst een eigen wachtwoord kiezen.
    if (passwordChangeRequired && !allowPasswordChange) {
        return <Navigate to="/wachtwoord" replace />;
    }
    return children;
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <ErrorBoundary>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <AuthProvider
                        endpoints={{
                            verify: apiEndpoints.me,
                            login: apiEndpoints.login,
                            logout: apiEndpoints.logout,
                            roleConfig: apiEndpoints.me,
                            loginPath: '/login',
                        }}
                        fetcher={authFetcher}
                        refreshOnMount
                    >
                        <Routes>
                            <Route path="/login" element={<LoginPage />} />
                            <Route
                                path="/"
                                element={<RequireAuth><ProjectsListPage /></RequireAuth>}
                            />
                            <Route
                                path="/projects"
                                element={<Navigate to="/" replace />}
                            />
                            <Route
                                path="/projects/new"
                                element={<RequireAuth><NewProjectPage /></RequireAuth>}
                            />
                            <Route
                                path="/projects/:projectId"
                                element={<RequireAuth><WizardPage /></RequireAuth>}
                            >
                                <Route path="wizard" element={<WizardIndex />} />
                                <Route path="wizard/:clusterId" element={<ClusterRunnerPage />} />
                                <Route path="risks" element={<RiskRegisterPage />} />
                                <Route path="report" element={<ReportPage />} />
                            </Route>
                            <Route
                                path="/wachtwoord"
                                element={<RequireAuth allowPasswordChange><ChangePasswordPage /></RequireAuth>}
                            />
                            <Route
                                path="/accounts"
                                element={<RequireAuth><AccountsPage /></RequireAuth>}
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </AuthProvider>
                </BrowserRouter>
            </ErrorBoundary>
        </ThemeProvider>
    );
}

startVersionCheck();

const container = document.getElementById('root');
createRoot(container).render(
    <StrictMode>
        <App />
    </StrictMode>
);
